/**
 * Browser client for the chat BFF. Knows nothing about React or any widget.
 *
 * Kept free of widget types on purpose. The mapping from these frames onto a
 * particular widget's event vocabulary is a handful of lines and belongs next to
 * that widget; everything genuinely hard — conversation lifecycle, SSE framing,
 * the 404 recovery, error identity, abort propagation — is here, testable without
 * a DOM and unaffected by which widget version the portal ends up consuming.
 *
 * All requests are same-origin. The credential is an HttpOnly cookie the portal's
 * nginx makes first-party (devops/templates/10-chat-proxy.conf.template), so
 * nothing here holds or even sees a token, and there is no `apiKey` to leak
 * through devtools.
 */

// Same origin. The BFF owns the /chat prefix, and it must stay a relative URL:
// an absolute one would make the request cross-site again and the cookie would
// stop being sent.
const BASE = process.env.REACT_APP_CHAT_BASE_URL || '';

// Mirrors CHAT_MESSAGE_MAX_CHARS on the BFF and the agent. Checked here so an
// oversized message fails instantly with a clear message instead of costing a
// round trip to be told the same thing.
export const MESSAGE_MAX_CHARS = 16000;

/**
 * Public message per error code.
 *
 * Branching on `code` and never on the server's `message`: the codes are a stable
 * contract, the strings are not. Support needs the request id on screen, so every
 * message that has one ends with it.
 */
const ERROR_MESSAGES = {
  UNAUTHENTICATED:
    'Sua sessão expirou. Informe seu e-mail novamente para continuar a conversa.',
  AUTH_UNAVAILABLE:
    'O serviço de autenticação está indisponível. Tente novamente em alguns instantes.',
  CHAT_NOT_FOUND: 'Esta conversa não está mais disponível. Inicie uma nova conversa.',
  INVALID_REQUEST: 'Não foi possível enviar sua mensagem. Tente reformulá-la.',
  INVALID_MESSAGE: 'Mensagem inválida. Verifique o texto e tente novamente.',
  CHAT_BUSY: 'Ainda estou preparando a resposta anterior. Aguarde um momento.',
  // No automatic retry on either 429: the agent allows four concurrent runs in the
  // whole deployment, and a client that retries on its own turns a busy moment
  // into a stampede.
  CAPACITY_EXCEEDED:
    'O assistente está atendendo outras pessoas neste momento. Tente novamente em alguns segundos.',
  QUOTA_EXCEEDED:
    'Você atingiu o limite de perguntas deste período. Tente novamente mais tarde.',
  CONVERSATION_LIMIT_EXCEEDED:
    'Você atingiu o limite de conversas abertas. Encerre uma conversa para iniciar outra.',
  RUN_TIMEOUT:
    'A resposta demorou mais do que o esperado e foi interrompida. Tente uma pergunta mais específica.',
  AGENT_UNAVAILABLE: 'O assistente não conseguiu responder. Tente novamente.',
  INTERNAL_ERROR: 'Ocorreu um erro inesperado. Tente novamente.',
};

const FALLBACK_MESSAGE = 'Ocorreu um erro inesperado. Tente novamente.';

/** Human-readable text for a code, with the request id appended when known. */
export function messageFor(code, requestId) {
  const base = ERROR_MESSAGES[code] || FALLBACK_MESSAGE;
  return requestId ? `${base} (ref: ${requestId})` : base;
}

/** An error carrying the server's stable code and request id, not just a string. */
export class ChatError extends Error {
  constructor(code, requestId) {
    super(messageFor(code, requestId));
    this.name = 'ChatError';
    this.code = code;
    this.requestId = requestId || '';
  }
}

/**
 * Turn a failed response into a `ChatError`.
 *
 * The body is the primary source and the status is the fallback, because a
 * response that never reached the BFF — a proxy error page, a severed connection —
 * has a meaningful status and no envelope at all.
 */
async function toError(response) {
  let code = 'INTERNAL_ERROR';
  let requestId = response.headers.get('X-Request-ID') || '';

  try {
    const body = await response.json();
    if (body && body.error) {
      if (body.error.code) code = body.error.code;
      if (body.error.request_id) requestId = body.error.request_id;
    }
  } catch (parseFailure) {
    // Non-JSON body. The status below still classifies it.
    if (response.status === 401) code = 'UNAUTHENTICATED';
    else if (response.status === 404) code = 'CHAT_NOT_FOUND';
    else if (response.status === 429) code = 'CAPACITY_EXCEEDED';
    else if (response.status === 504) code = 'RUN_TIMEOUT';
    else if (response.status >= 502) code = 'AGENT_UNAVAILABLE';
  }

  return new ChatError(code, requestId);
}

/** Options shared by every request: same-origin cookie, no cache. */
function requestInit(signal, extra = {}) {
  return {
    // The whole authentication scheme. Without it the browser withholds the
    // visitor cookie and every call is a 401.
    credentials: 'include',
    cache: 'no-store',
    signal,
    ...extra,
  };
}

/** `POST /chat/conversations` — the server mints the id; clients cannot choose it. */
export async function createConversation(signal) {
  const response = await fetch(
    `${BASE}/chat/conversations`,
    requestInit(signal, { method: 'POST' })
  );
  if (!response.ok) throw await toError(response);
  const body = await response.json();
  return body.chat_id;
}

/** `DELETE /chat/conversations/{id}` — the only deletion path that exists. */
export async function deleteConversation(chatId, signal) {
  const response = await fetch(
    `${BASE}/chat/conversations/${encodeURIComponent(chatId)}`,
    requestInit(signal, { method: 'DELETE' })
  );
  // 404 means it is already gone, which is the state the caller asked for.
  if (!response.ok && response.status !== 404) throw await toError(response);
}

/**
 * `GET /chat/session` — does the browser still hold a valid visitor credential?
 *
 * The cookie is HttpOnly, so this is the only way the page can find out. Returns
 * null rather than throwing on 401, because "not signed in" is an expected state
 * on a cold page load, not a failure.
 */
export async function getSession(signal) {
  const response = await fetch(`${BASE}/chat/session`, requestInit(signal));
  if (response.status === 401) return null;
  if (!response.ok) throw await toError(response);
  return response.json();
}

/**
 * Split an SSE body into `{event, data}` objects.
 *
 * Frames are separated by a blank line, and a chunk boundary can fall anywhere —
 * including mid-frame — so the buffer has to persist across reads. Splitting on
 * every newline instead would emit half-frames under exactly the conditions this
 * stream is designed for.
 *
 * An unparseable frame is dropped rather than thrown: it must not kill a turn that
 * is otherwise progressing, and the terminal frame is what decides the outcome.
 */
export async function* readSseFrames(response) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let split;
      while ((split = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);

        let event = 'message';
        const data = [];
        for (const line of block.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) data.push(line.slice(5).trim());
        }
        if (!data.length) continue;

        try {
          yield { event, data: JSON.parse(data.join('\n')) };
        } catch (unreadable) {
          // Dropped on purpose. See above.
        }
      }
    }
  } finally {
    // Releasing the lock lets the connection be torn down promptly on abort,
    // which is what frees the agent's capacity slot.
    try {
      reader.releaseLock();
    } catch (alreadyReleased) {
      /* nothing to do */
    }
  }
}

async function openStream(chatId, message, signal) {
  return fetch(
    `${BASE}/chat/conversations/${encodeURIComponent(chatId)}/messages/stream`,
    requestInit(signal, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      // Only this field. Upstream schemas set extra="forbid", so any additional
      // key is a guaranteed 422.
      body: JSON.stringify({ message }),
    })
  );
}

/**
 * Send one message and yield the turn's progress.
 *
 * Yields, in order:
 *   `{type: 'chat', chatId}`      when a conversation was created or replaced
 *   `{type: 'started', requestId}` once, immediately
 *   `{type: 'elapsed', elapsedMs}` per heartbeat
 *   then exactly one of
 *   `{type: 'answer', text, messageId}` or `{type: 'failed', error}`
 *
 * `answer` is a complete reply, not a delta. The agent produces one finished
 * answer at the end of the turn — anywhere from ~7s to ~139s, with a 285s ceiling —
 * so a consumer must replace the pending message rather than append to it.
 *
 * `resolveChatId` is a callback rather than a plain id so this function can own the
 * 404 recovery: a restored transcript can carry a chat id the server has since
 * forgotten, and the only sensible response is to create a fresh conversation and
 * send the message there. The caller learns about the swap through the `chat`
 * event.
 */
export async function* sendMessage({ message, resolveChatId, signal }) {
  const text = String(message == null ? '' : message);
  if (!text.trim()) {
    yield { type: 'failed', error: new ChatError('INVALID_MESSAGE', '') };
    return;
  }
  if (text.length > MESSAGE_MAX_CHARS) {
    // Enforced before the request so the visitor is told immediately, and so an
    // oversized body never occupies the connection.
    yield { type: 'failed', error: new ChatError('INVALID_MESSAGE', '') };
    return;
  }

  let chatId = await resolveChatId();
  if (!chatId) {
    chatId = await createConversation(signal);
    yield { type: 'chat', chatId };
  }

  let response = await openStream(chatId, text, signal);

  if (response.status === 404) {
    // The server has forgotten this conversation. Recreate once; a second 404
    // would mean something else is wrong and is reported as an error.
    chatId = await createConversation(signal);
    yield { type: 'chat', chatId };
    response = await openStream(chatId, text, signal);
  }

  if (!response.ok || !response.body) {
    yield { type: 'failed', error: await toError(response) };
    return;
  }

  for await (const { event, data } of readSseFrames(response)) {
    if (event === 'run.started') {
      yield { type: 'started', requestId: data.request_id };
    } else if (event === 'heartbeat') {
      // The only honest progress signal there is. The frame carries elapsed time
      // and nothing else, so a percentage bar would be an invention.
      yield { type: 'elapsed', elapsedMs: data.elapsed_ms };
    } else if (event === 'message.completed') {
      yield {
        type: 'answer',
        text: data.message.content,
        messageId: data.message.message_id,
      };
      return;
    } else if (event === 'error') {
      yield {
        type: 'failed',
        error: new ChatError(data.error.code, data.error.request_id),
      };
      return;
    }
  }

  // The stream ended without a terminal frame: a dropped connection or a proxy
  // that closed the response. Reported rather than left as a silent pending state.
  yield {
    type: 'failed',
    error: new ChatError('AGENT_UNAVAILABLE', ''),
  };
}
